import AdjustOutlinedIcon from '@mui/icons-material/AdjustOutlined';
import ExposureOutlinedIcon from '@mui/icons-material/ExposureOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { Study, StudyWorkflowModules } from 'interfaces/study';
import { map } from 'lodash';
import React from 'react';
import useStudy from 'utils/useStudy';

import { StudyDashboardTabTableLayout } from './StudyDashboardTabTableLayout';
import { getConfiguredStudyWorkflowModules } from './Workflow/multiplexWorkflowSteps';
import { StudyWorkflowMonitor } from './Workflow/StudyWorkflowMonitor';

const workflowModuleIcons: Record<keyof StudyWorkflowModules, React.ReactElement> = {
  rnaProbes: <AdjustOutlinedIcon />,
  markerPositivity: <ExposureOutlinedIcon />,
  cellRulesBasedCellTyping: <RuleOutlinedIcon />,
};

interface Props {
  advancedSearchDrawerOpen: boolean;
  study?: Study;
  isMultiplexStudy: boolean;
}

const StudyDashboardWorkflowView: React.FC<React.PropsWithChildren<Props>> = ({
  advancedSearchDrawerOpen,
  isMultiplexStudy,
  study,
}) => {
  // The Workflow tab's study is loaded without statistics, which the export buttons need (hasResults, hasFeatures
  // and the unpublished counterparts), so the same study is read once more with them.
  const {
    study: studyWithStatistics,
    isLoading: isLoadingStatistics,
    isPlaceholderData: isStatisticsPlaceholder,
    isError: hasStatisticsError,
  } = useStudy(study?.id, { enabled: Boolean(study?.id), withStatistics: true });
  const workflowStudy = studyWithStatistics ? { ...study, ...studyWithStatistics } : study;
  // A cached study stands in for the real read; without statistics it must not read as "no results to export".
  const isLoadingExportAvailability = Boolean(study?.id) && (isLoadingStatistics || Boolean(isStatisticsPlaceholder));
  const configuredModules = getConfiguredStudyWorkflowModules({
    modules: workflowStudy?.settings?.workflow?.modules,
  });

  return (
    <StudyDashboardTabTableLayout
      leftDrawerOpen={advancedSearchDrawerOpen}
      useOwnScroll={false}
      toolbar={
        <Stack spacing={0.5} sx={{ px: 0.5, pt: 0.5, pb: 0.5 }}>
          <Typography variant="h4">Study workflow</Typography>
          {configuredModules.length ? (
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="caption" color="text.secondary">
                Configured
              </Typography>
              {map(configuredModules, ({ key, label }) => (
                <Chip key={key} size="small" variant="outlined" icon={workflowModuleIcons[key]} label={label} />
              ))}
            </Stack>
          ) : null}
        </Stack>
      }
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'visible' }}>
        <StudyWorkflowMonitor
          study={workflowStudy}
          isMultiplexStudy={isMultiplexStudy}
          isLoadingExportAvailability={isLoadingExportAvailability}
          hasExportAvailabilityError={Boolean(hasStatisticsError)}
        />
      </Box>
    </StudyDashboardTabTableLayout>
  );
};

export default StudyDashboardWorkflowView;
